import { message, Spin, Tooltip } from 'antd';
import RadioCards from 'components/RadioCards/RadioCards';
import { Banner } from 'components/ui/Banner';
import { useCallback, useEffect, useState } from 'react';
import { commonService, familyService } from 'services';
import { FAMILY_APPLICATION_STEPS, FAMILY_BRANCH_MAP, FAMILY_STATUS_MAP } from '../../familyApplicationConstants';
import { useAsync } from 'react-async-hook';
import PageMeta from 'components/PageMeta/PageMeta';
import { FAMILY_COMPONENT_MAP, getComponentById } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getApplicantFirstName } from '../../utils';
import { updateMilitaryField } from '../../utils/militaryDataCleaning';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import { InformationFilled } from '@carbon/icons-react';

export function FamilyApplicationComponent({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [selectedOption, setSelectedOption] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isCompletedBySponsor = application?.completedBySponsor === true;

  // API fetches
  const { result: componentOptions = null } = useAsync(async () => {
    if (application?.applicant?.militaryDetails?.branch?.id === undefined) {
      return null;
    }

    return commonService.get(
      `/families/ref-data/military-info/components?branchId=${application?.applicant?.militaryDetails?.branch?.id}`,
      {
        cache: { interpretHeader: false },
      },
    );
  }, [application]);

  const onSubmit = useCallback(async () => {
    const _goToNextStep = (_newApplication) => {
      // Use getNextStep to determine the next step
      const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.COMPONENT, _newApplication, stepConfig);
      setStep({
        applicationId: _newApplication?.id,
        newStep: nextStep,
      });
    };

    if (readOnly) {
      _goToNextStep(application);
      return;
    }

    try {
      setSubmitting(true);

      const _isCivilian = selectedOption === FAMILY_COMPONENT_MAP.civilianEmployee.id;

      // FAMILY-APPS__DATA_CLEANING - Only apply cleaning if militaryComponent actually changed
      const currentComponentId = application?.applicant?.militaryDetails?.militaryComponent?.id;
      const componentChanged = currentComponentId !== selectedOption;

      let updatedMilitaryDetails = componentChanged
        ? updateMilitaryField({ fieldName: 'militaryComponent', newValue: selectedOption, application })
        : { ...application?.applicant?.militaryDetails, militaryComponent: { id: selectedOption } };

      // Special handling for civilian component - set status and preserve certain fields
      if (_isCivilian) {
        updatedMilitaryDetails = {
          ...updatedMilitaryDetails,
          // if civilian, the 'status' step is skipped so we need to set the status to civilian
          militaryStatus: FAMILY_STATUS_MAP.civilianEmployee,
          // if not civilian, the 'payment system' step is skipped
          paymentSystem: application?.applicant?.militaryDetails?.paymentSystem,
          // if civilian, the 'rank' step is skipped - keep fields null
          grade: null,
          gradeUnknown: null,
          yearsOfService: null,
        };
      }

      const _newSponsorData = await familyService.addUpdateAdult(household?.id, application?.applicant?.id ?? null, {
        ...application?.applicant,
        militaryDetails: updatedMilitaryDetails,
      });

      const _newApplication = await familyService.saveApplication({
        ...application,
        applicant: {
          ...application?.applicant,
          ..._newSponsorData,
        },
      });

      setApplication(_newApplication);
      _goToNextStep(_newApplication);
    } catch (error) {
      message.error('Something went wrong');
      newrelic.noticeError(error);
    } finally {
      setSubmitting(false);
    }
  }, [application, household?.id, readOnly, selectedOption, setApplication, setStep, stepConfig]);

  // Set the selected option based on the application data
  useEffect(() => {
    if (application?.applicant?.militaryDetails?.militaryComponent?.id !== undefined) {
      setSelectedOption(application?.applicant?.militaryDetails?.militaryComponent?.id);
    }
  }, [application?.applicant?.militaryDetails?.militaryComponent?.id]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={componentOptions === null}>
      <PageMeta title="Component" />

      <div className="p-7 pt-0">
        <Banner
          question={
            isCompletedBySponsor
              ? 'How do you serve?'
              : `How does ${getApplicantFirstName(application?.applicant)} serve?`
          }
          className="my-12"
        />

        <RadioCards
          options={componentOptions?.map((_option) => {
            const _componentDetails = getComponentById(_option.id);

            let _description;
            let _title;

            /*
             * Set title & description
             */
            // Check if this is a civilian employee option
            if (_option.id === FAMILY_COMPONENT_MAP.civilianEmployee.id) {
              _title = 'Civilian Employee';

              // If branch title exists, use custom description with branch name
              if (application?.applicant?.militaryDetails?.branch?.title) {
                // Transform branch title for special cases
                let departmentName = application?.applicant?.militaryDetails?.branch?.title;
                const branchId = application?.applicant?.militaryDetails?.branch?.id;

                // Marine Corps → Department of the Navy (Marine Corps)
                if (branchId === FAMILY_BRANCH_MAP.marineCorps.id) {
                  departmentName = 'Department of the Navy (Marine Corps)';
                }
                // Space Force → Department of the Air Force (Space Force)
                else if (branchId === FAMILY_BRANCH_MAP.spaceForce.id) {
                  departmentName = 'Department of the Air Force (Space Force)';
                }
                // Other branches use standard format
                else {
                  departmentName = `Department of the ${departmentName}`;
                }

                if (isCompletedBySponsor) {
                  _description = `I am a civilian employed by the ${departmentName}`;
                } else {
                  _description = `A civilian employed by the ${departmentName}`;
                }
              } else {
                // Otherwise use the default component description
                _description = _componentDetails.description;
              }
            } else {
              // For non-civilian options, get description based on branch and completion status
              const completionKey = isCompletedBySponsor ? 'completedBySponsor' : 'completedOnBehalfOfSponsor';
              const branchId = application?.applicant?.militaryDetails?.branch?.id;

              // Try to get branch-specific description, fallback to option description
              _description =
                _componentDetails?.descriptionBasedOnBranch?.[completionKey]?.[branchId] || _option.description;
            }

            /*
             * Set tooltip title
             */
            let _tooltipTitle = null;
            if (
              [FAMILY_BRANCH_MAP.army.id, FAMILY_BRANCH_MAP.airForce.id].includes(
                application?.applicant?.militaryDetails?.branch?.id,
              )
            ) {
              if (_option.id === FAMILY_COMPONENT_MAP.nationalGuard.id) {
                _tooltipTitle =
                  'National Guard members must be on Title 10 or Title 32 orders for a minimum of 30 consecutive days or Dual-Status Technicians who are currently not on orders.';
              } else if (_option.id === FAMILY_COMPONENT_MAP.reserve.id) {
                _tooltipTitle =
                  'Reserve members must be on Title 10 orders for a minimum of 30 consecutive days or Dual-Status Technicians who are currently not on orders.';
              }
            } else if (application?.applicant?.militaryDetails?.branch?.id === FAMILY_BRANCH_MAP.marineCorps.id) {
              if (_option.id === FAMILY_COMPONENT_MAP.reserve.id) {
                _tooltipTitle = 'Reserve members must be on Title 10 orders for a minimum of 30 consecutive days.';
              }
            }

            if (_tooltipTitle) {
              _description = (
                <Tooltip title={_tooltipTitle}>
                  <span className="flex items-center gap-2">
                    <span className="border-b border-dashed border-gray-400 cursor-help">{_description}</span>{' '}
                    <InformationFilled />
                  </span>
                </Tooltip>
              );
            }

            return {
              label:
                _title ||
                _componentDetails?.titleBasedOnBranch?.[application?.applicant?.militaryDetails?.branch?.id] ||
                _option.title,
              description: _description,
              value: _option.id,
            };
          })}
          value={selectedOption}
          onChange={(value) => {
            setSelectedOption(value);
          }}
          className="mt-8"
          disabled={readOnly}
        />
      </div>

      <ApplicationWizardActions
        applicationIsReadOnly={readOnly}
        backButtonProps={{
          disabled: submitting,
          onClick: () => {
            // Use getPreviousStep to determine the previous step
            const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.COMPONENT, application, stepConfig);

            setStep({
              applicationId: application?.id,
              newStep: previousStep,
            });
          },
        }}
        nextButtonProps={{
          disabled: !selectedOption,
          loading: submitting,
          onClick: onSubmit,
          'data-testid': 'next-button',
        }}
      />
    </Spin>
  );
}
